// src/pages/Student/StudentHome.jsx
// ── Duolingo-stiliaus Student Dashboard ──────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { useGamification } from '../../hooks/useGamification';
import { UsageWidget, UsageWidgetCompact } from '../../components/SubscriptionComponents';
import UpgradePrompt from '../../components/Subscription/UpgradePrompt';
import XPBar from '../../components/Gamification/XPBar';
import HeartsWidget from '../../components/Gamification/HeartsWidget';
import DailyGoalWidget from '../../components/Gamification/DailyGoalWidget';
import AchievementToast from '../../components/Gamification/AchievementToast';

// ── Žaidimų katalogas ─────────────────────────────────────────────────────────

const GAME_CATALOG = [
  // Nauji pagrindiniai žaidimai (Free)
  { id: 'shapes_sorting',       name: 'Figūrų rūšiavimas',       icon: '🔷', color: 'from-pink-500 to-purple-500',   subject: 'math',     accessLevel: 0 },
  { id: 'colors_matching',      name: 'Spalvų derinimas',        icon: '🎨', color: 'from-orange-500 to-red-500',    subject: 'other',    accessLevel: 0 },
  { id: 'number_matching',      name: 'Skaičių atitikimas',      icon: '🔢', color: 'from-blue-500 to-purple-500',   subject: 'math',     accessLevel: 0 },
  { id: 'word_builder',         name: 'Žodžių sudarymas',        icon: '📝', color: 'from-green-500 to-teal-500',    subject: 'language', accessLevel: 0 },
  { id: 'sequence_builder',     name: 'Sekos kūrimas',           icon: '🔢', color: 'from-indigo-500 to-blue-500',   subject: 'math',     accessLevel: 0 },
  { id: 'pattern_matching',     name: 'Šablonų derinimas',       icon: '🎭', color: 'from-pink-500 to-red-500',      subject: 'math',     accessLevel: 0 },
  { id: 'math_equation',        name: 'Matematinės lygtys',      icon: '➕', color: 'from-yellow-500 to-orange-500', subject: 'math',     accessLevel: 0 },
  { id: 'story_sequence',       name: 'Pasakojimo seka',         icon: '📖', color: 'from-purple-500 to-pink-500',   subject: 'language', accessLevel: 1 },

  // Matematika
  { id: 'number_line_jump',     name: 'Šokinėjimas ant tiesės', icon: '🦘', color: 'from-green-500 to-emerald-500',   subject: 'math',     accessLevel: 0 },
  { id: 'fraction_pizza',       name: 'Trupmenos su Pica',       icon: '🍕', color: 'from-orange-500 to-red-500',     subject: 'math',     accessLevel: 1 },
  { id: 'math_bingo',           name: 'Matematikos Bingo',       icon: '🎲', color: 'from-purple-500 to-pink-500',    subject: 'math',     accessLevel: 1 },
  { id: 'money_shop',           name: 'Pinigų Parduotuvė',       icon: '💰', color: 'from-green-500 to-yellow-500',   subject: 'math',     accessLevel: 1 },
  { id: 'time_clock',           name: 'Laikrodžio Meistras',     icon: '⏰', color: 'from-blue-500 to-indigo-500',    subject: 'math',     accessLevel: 1 },
  { id: 'geometry_builder',     name: 'Geometrijos Statytojas',  icon: '📐', color: 'from-pink-500 to-purple-500',    subject: 'math',     accessLevel: 1 },
  { id: 'multiplication_race',  name: 'Dauginimo Lenktynės',     icon: '🏁', color: 'from-orange-500 to-red-500',    subject: 'math',     accessLevel: 1 },
  { id: 'chart_maker',          name: 'Grafikų Kūrėjas',         icon: '📊', color: 'from-blue-500 to-green-500',    subject: 'math',     accessLevel: 1 },
  { id: 'story_math',           name: 'Pasakojimų Matematika',   icon: '📖', color: 'from-yellow-500 to-orange-500', subject: 'math',     accessLevel: 1 },
  { id: 'mystery_math_detective',name: 'Matematikos Detektyvas', icon: '🔍', color: 'from-gray-600 to-gray-800',     subject: 'math',     accessLevel: 2 },
  { id: 'recipe_math_chef',     name: 'Recepto Matematika',      icon: '👨‍🍳', color: 'from-red-400 to-orange-400',   subject: 'math',     accessLevel: 2 },
  { id: 'math_art',             name: 'Matematikos Menas',       icon: '🎨', color: 'from-pink-400 to-purple-400',   subject: 'math',     accessLevel: 2 },

  // Lietuvių kalba
  { id: 'alphabet_adventure',   name: 'Abėcėlės Nuotykis',       icon: '🔤', color: 'from-green-500 to-teal-500',    subject: 'language', accessLevel: 0 },
  { id: 'vowel_consonant',      name: 'Balsės vs Priebalsės',    icon: '🅰️', color: 'from-red-500 to-blue-500',      subject: 'language', accessLevel: 0 },
  { id: 'syllable_sorter',      name: 'Skiemenų Rūšiuotojas',    icon: '🎵', color: 'from-pink-500 to-rose-500',     subject: 'language', accessLevel: 0 },
  { id: 'sentence_builder',     name: 'Sakinių Sudarymas',       icon: '🏗️', color: 'from-green-500 to-blue-500',    subject: 'language', accessLevel: 0 },
  { id: 'rhyme_time',           name: 'Rimų Laikas',             icon: '🎤', color: 'from-purple-500 to-indigo-500', subject: 'language', accessLevel: 1 },
  { id: 'story_scramble',       name: 'Pasakos Sudėliojimas',    icon: '📚', color: 'from-purple-500 to-pink-500',   subject: 'language', accessLevel: 1 },
  { id: 'word_family_tree',     name: 'Žodžių Šeimos Medis',     icon: '🌳', color: 'from-green-600 to-green-400',   subject: 'language', accessLevel: 1 },
  { id: 'capital_letter_detective', name: 'Didžiosios Raidės',   icon: '🔎', color: 'from-blue-600 to-indigo-600',  subject: 'language', accessLevel: 1 },
  { id: 'word_hunt',            name: 'Žodžių Medžioklė',        icon: '🔍', color: 'from-amber-500 to-orange-500',  subject: 'language', accessLevel: 1 },
  { id: 'reading_comprehension',name: 'Skaitymo Supratimas',     icon: '📖', color: 'from-teal-500 to-cyan-500',    subject: 'language', accessLevel: 1 },
  { id: 'speed_reading_racer',  name: 'Greito Skaitymo Lenktynės',icon: '⚡', color: 'from-yellow-500 to-amber-500', subject: 'language', accessLevel: 1 },
  { id: 'punctuation_police',   name: 'Skyrybos Policija',       icon: '🚔', color: 'from-blue-700 to-blue-500',    subject: 'language', accessLevel: 1 },
  { id: 'ai_story_cowriter',    name: 'AI Pasakų Rašytojas',     icon: '✍️', color: 'from-violet-500 to-purple-500', subject: 'language', accessLevel: 2 },
  { id: 'debate_arena',         name: 'Diskusijų Arena',         icon: '🎭', color: 'from-red-600 to-rose-500',     subject: 'language', accessLevel: 2 },
  { id: 'word_number_puzzle',   name: 'Žodžių Skaičių Dėlionė',  icon: '🧩', color: 'from-indigo-500 to-blue-500',  subject: 'language', accessLevel: 1 },

  // Logopedija
  { id: 'minimal_pairs_memory', name: 'Minimalūs Poreliai',      icon: '👂', color: 'from-teal-500 to-green-500',   subject: 'speech',   accessLevel: 1 },
  { id: 'sound_position_finder',name: 'Garsų Ieškotojas',        icon: '🔊', color: 'from-cyan-500 to-blue-500',    subject: 'speech',   accessLevel: 1 },
  { id: 'syllable_clap',        name: 'Skiemenų Plojimas',       icon: '👏', color: 'from-pink-500 to-rose-400',    subject: 'speech',   accessLevel: 2 },
  { id: 'tongue_twister_challenge', name: 'Liežuvio Suktiniai', icon: '🦁', color: 'from-orange-500 to-amber-500', subject: 'speech',   accessLevel: 2 },
  { id: 'breathing_dragon',     name: 'Kvėpavimo Drakonas',      icon: '🐉', color: 'from-green-600 to-teal-600',   subject: 'speech',   accessLevel: 2 },
  { id: 'sight_word_bingo',     name: 'Žodžių Bingo',            icon: '👁️', color: 'from-blue-400 to-cyan-400',    subject: 'speech',   accessLevel: 1 },
  { id: 'sound_safari',         name: 'Garsų Safaras',           icon: '🦒', color: 'from-yellow-500 to-amber-400', subject: 'speech',   accessLevel: 0 },

  // Kita
  { id: 'simon_says',           name: 'Simonas Sako',            icon: '🎯', color: 'from-indigo-500 to-purple-500',subject: 'other',    accessLevel: 1 },
  { id: 'shape_yoga',           name: 'Figūrų Joga',             icon: '🧘', color: 'from-green-400 to-teal-400',   subject: 'other',    accessLevel: 1 },
  { id: 'emotion_charades',     name: 'Emocijų Šaradės',         icon: '😊', color: 'from-pink-400 to-rose-400',   subject: 'other',    accessLevel: 1 },
  { id: 'animal_habitat',       name: 'Gyvūnų Buveinės',         icon: '🦌', color: 'from-green-700 to-green-500', subject: 'other',    accessLevel: 1 },
  { id: 'weather_station',      name: 'Oro Stotis',              icon: '🌤️', color: 'from-sky-400 to-blue-400',    subject: 'other',    accessLevel: 1 },
  { id: 'veterinarian_clinic',  name: 'Veterinarijos Klinika',   icon: '🐾', color: 'from-teal-600 to-green-600',  subject: 'other',    accessLevel: 1 },
  { id: 'store_cashier',        name: 'Parduotuvės Kasininkas',  icon: '🛒', color: 'from-green-500 to-lime-500',   subject: 'other',    accessLevel: 1 },
  { id: 'time_travel_history',  name: 'Istorijos Laiko Mašina',  icon: '⏳', color: 'from-amber-600 to-yellow-600', subject: 'other',   accessLevel: 2 },
  { id: 'geography_treasure_hunt', name: 'Geografiniai Lobiai',  icon: '🗺️', color: 'from-blue-600 to-indigo-700', subject: 'other',    accessLevel: 2 },
  { id: 'science_lab',          name: 'Virtualus Mokslo Lab.',   icon: '🔬', color: 'from-purple-600 to-violet-600',subject: 'other',   accessLevel: 2 },
  { id: 'ai_coding_blocks',     name: 'AI Kodavimo Blokai',      icon: '💻', color: 'from-gray-700 to-gray-900',   subject: 'other',    accessLevel: 2 },
  { id: 'dream_builder',        name: 'Svajonių Kūrėjas',        icon: '💭', color: 'from-violet-400 to-purple-400',subject: 'other',   accessLevel: 2 },
  { id: 'character_detective',  name: 'Personažo Detektyvas',    icon: '🕵️', color: 'from-slate-600 to-slate-800', subject: 'other',    accessLevel: 1 },
  { id: 'teacher_helper',       name: 'Mokytojo Asistentas',     icon: '👩‍🏫', color: 'from-blue-500 to-cyan-500',  subject: 'other',    accessLevel: 1 },
  { id: 'note_name',            name: 'Natų Vardai',             icon: '🎵', color: 'from-purple-400 to-pink-400',  subject: 'other',    accessLevel: 1 },
  { id: 'rhythm_maker',         name: 'Ritmo Kūrėjas',           icon: '🥁', color: 'from-red-500 to-pink-500',    subject: 'other',    accessLevel: 1 },
  { id: 'kuciu_stalas',         name: 'Kūčių Stalas',            icon: '🎄', color: 'from-red-700 to-green-700',   subject: 'other',    accessLevel: 2 },
  { id: 'custom',               name: 'Pasirinktinis Žaidimas',  icon: '✨', color: 'from-violet-600 to-pink-600', subject: 'other',    accessLevel: 2 },
];

const SUBJECTS = [
  { id: 'all',      label: 'Visi',       icon: '🎮' },
  { id: 'math',     label: 'Matematika', icon: '🔢' },
  { id: 'language', label: 'Lietuvių',   icon: '📝' },
  { id: 'speech',   label: 'Logopedija', icon: '🎤' },
  { id: 'other',    label: 'Kita',       icon: '🌟' },
];

// ── Komponentas ───────────────────────────────────────────────────────────────

export default function StudentHome() {
  const { user, logout } = useAuth();
  const { usage, checkLimit } = useSubscription();
  const { profile, newAchievements, handleGameResult, loseHeart, clearNewAchievements } = useGamification();
  const navigate = useNavigate();

  const [activeSubject, setActiveSubject] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [upgradeType, setUpgradeType] = useState(null); // 'limit' | 'locked'
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/dashboard/student`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  // Access level iš subscription
  const userAccessLevel = usage?.access_level ?? 0;

  const isLocked = useCallback(
    (gameAccessLevel) => userAccessLevel < gameAccessLevel,
    [userAccessLevel]
  );

  const playGame = useCallback(
    (game) => {
      if (isLocked(game.accessLevel)) {
        setUpgradeType('locked');
        return;
      }
      if (!checkLimit()) {
        setUpgradeType('limit');
        return;
      }
      navigate('/ai-games', { state: { autoStartGame: game.id } });
    },
    [isLocked, checkLimit, navigate]
  );

  // Filtruoti žaidimai
  const filteredGames = useMemo(() => {
    return GAME_CATALOG.filter(g => {
      const matchSubject = activeSubject === 'all' || g.subject === activeSubject;
      const matchSearch = !searchQuery || g.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchSubject && matchSearch;
    });
  }, [activeSubject, searchQuery]);

  // Top recommended (unlocked + laisvai)
  const recommendedGames = useMemo(() =>
    GAME_CATALOG.filter(g => !isLocked(g.accessLevel)).slice(0, 3),
    [isLocked]
  );

  return (
    <div className="min-h-screen pb-12" style={{ background: '#F4F7FB', fontFamily: 'Nunito, sans-serif' }}>

      {/* Achievement Toast */}
      <AchievementToast
        achievements={newAchievements}
        onDismiss={clearNewAchievements}
      />

      {/* ── HEADER (Kids Premium Floating Layout) ───────────────────────────── */}
      <header className="dashboard-header">
        <div className="header-bubble">

        {/* Row 1: Logo + Badge indicators + Actions */}
        <div className="dashboard-header-row1">

          {/* Left: Logo */}
          <div className="flex items-center gap-2 cursor-pointer flex-shrink-0 group" onClick={() => navigate('/dashboard')}>
            <span className="text-[2.2rem] group-hover:-translate-y-1 group-hover:rotate-6 transition-transform duration-300">🐼</span>
            <span className="text-2xl font-black hidden sm:block tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-gray-500">
              SmartPanda
            </span>
          </div>

          {/* Center / Right: Gamification badges */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">

            {/* Streak badge */}
            {profile && (
              <div
                className={`stat-badge stat-badge-streak ${profile.streak_days === 0 ? 'inactive' : ''}`}
                title={`${profile.streak_days} dienų serija`}
              >
                <span>🔥</span>
                <span>{profile.streak_days}</span>
              </div>
            )}

            {/* Hearts badge */}
            {profile && (
              <div
                className={`stat-badge stat-badge-hearts ${(profile.hearts ?? 5) <= 1 ? 'critical' : ''}`}
                title={`${profile.hearts}/${profile.max_hearts} gyvybės${profile.hearts_refill_in_minutes ? ` | +1 po ${profile.hearts_refill_in_minutes} min.` : ''}`}
              >
                <span>❤️</span>
                <span className="font-black">{profile.hearts ?? 5}</span>
                {profile.hearts < profile.max_hearts && profile.hearts_refill_in_minutes && (
                  <span className="text-[10px] opacity-70">({profile.hearts_refill_in_minutes}m)</span>
                )}
              </div>
            )}

            {/* Daily goal badge */}
            {profile?.daily && (
              <div
                className={`stat-badge stat-badge-daily ${profile.daily.completed ? 'completed' : ''}`}
                title={`Dienos tikslas: ${profile.daily.earned_xp}/${profile.daily.target_xp} XP`}
              >
                <span>{profile.daily.completed ? '🎯' : '⭕'}</span>
                <span>{profile.daily.percentage ?? 0}%</span>
              </div>
            )}

            {/* AI Usage compact */}
            <UsageWidgetCompact usage={usage} />

            {/* Logout */}
            <button
              onClick={logout}
              className="stat-badge hover:bg-red-50 hover:text-red-500 text-gray-400 group"
              style={{ border: '2px solid transparent' }}
              title="Atsijungti"
            >
              <span className="text-lg group-hover:translate-x-0.5 transition-transform">🚪</span>
              <span className="hidden lg:block uppercase tracking-wider text-[11px]">Išeiti</span>
            </button>
          </div>
        </div>

        {/* Row 2: XP Bar (full-width) */}
        {profile && (
          <div className="dashboard-header-row2">
            <XPBar
              xp={profile.xp}
              level={profile.level}
              xpInLevel={profile.xp_in_current_level}
              xpToNextLevel={profile.xp_to_next_level}
              progressPct={profile.level_progress_pct}
              compact
            />
          </div>
        )}
        </div>
      </header>

      {/* ── MAIN ────────────────────────────────────────────────────── */}
      <main className="max-w-6xl mx-auto px-4 py-8">

        {/* Sveikinimas */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl font-black text-gray-800">
            Labas, {user?.name?.split(' ')[0] || 'Mokinys'}! 👋
          </h1>
          <p className="text-gray-500 font-bold mt-1">
            {profile?.daily?.completed
              ? '🎯 Dienos tikslas pasiektas! Žaisk daugiau!'
              : `Šiandien surinkta ${profile?.daily?.earned_xp ?? 0} / ${profile?.daily?.target_xp ?? 50} XP`}
          </p>
        </div>

        {/* ── Stats juosta ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: '⭐', value: stats?.total_score ?? 0, label: 'Taškai', color: '#FFC800' },
            { icon: '🏆', value: stats?.total_lessons_completed ?? 0, label: 'Pamokos', color: '#1CB0F6' },
            { icon: '🔥', value: profile?.streak_days ?? 0, label: 'Serija', color: '#FF9600' },
            { icon: '⚡', value: `L${profile?.level ?? 1}`, label: 'Lygis', color: '#CE82FF' },
          ].map(s => (
            <div key={s.label} className="stat-card animate-slide-up">
              <div className="text-3xl mb-1">{s.icon}</div>
              <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* ── AI Naudojimo widget (pilnas) ──────────────────────── */}
        <div className="mb-8">
          <UsageWidget usage={usage} />
        </div>

        {/* ── Rekomenduojami žaidimai ────────────────────────────── */}
        {recommendedGames.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-black text-gray-800 mb-4 flex items-center gap-2">
              <span>🚀</span> Rekomenduojami tau
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recommendedGames.map((game, idx) => (
                <button
                  key={game.id}
                  onClick={() => playGame(game)}
                  className="card-game text-left overflow-hidden p-0 border-0 w-full"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className={`bg-gradient-to-r ${game.color} p-5 text-white`}>
                    <div className="text-4xl mb-2">{game.icon}</div>
                    <h3 className="font-black text-lg leading-tight">{game.name}</h3>
                  </div>
                  <div className="px-5 py-3 flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-500">
                      {SUBJECTS.find(s => s.id === game.subject)?.label}
                    </span>
                    <span
                      className="text-xs font-black px-3 py-1 rounded-full text-white"
                      style={{ background: '#58CC02' }}
                    >
                      Žaisti →
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── Subject filtrų tab'ai ──────────────────────────────── */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {SUBJECTS.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSubject(s.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full font-black text-sm whitespace-nowrap transition-all flex-shrink-0"
              style={{
                background: activeSubject === s.id ? '#58CC02' : 'white',
                color: activeSubject === s.id ? 'white' : '#5C5C5C',
                border: `2px solid ${activeSubject === s.id ? '#46A302' : '#E5E5E5'}`,
                boxShadow: activeSubject === s.id ? '0 4px 12px rgba(88,204,2,0.3)' : 'none',
              }}
            >
              <span>{s.icon}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Paieška */}
        <div className="mb-6 relative">
          <input
            type="text"
            placeholder="🔍 Ieškoti žaidimo..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full px-5 py-3 rounded-2xl border-2 border-gray-200 font-bold text-gray-700 focus:outline-none focus:border-green-400 transition"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          />
        </div>

        {/* ── Žaidimų tinklelis ──────────────────────────────────── */}
        <section>
          <h2 className="text-xl font-black text-gray-800 mb-4 flex items-center gap-2">
            <span>🎮</span>
            {activeSubject === 'all' ? 'Visi žaidimai' : SUBJECTS.find(s => s.id === activeSubject)?.label}
            <span className="text-sm font-bold text-gray-400">({filteredGames.length})</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredGames.map((game) => {
              const locked = isLocked(game.accessLevel);
              return (
                <button
                  key={game.id}
                  onClick={() => playGame(game)}
                  className="card-game text-left overflow-hidden p-0 border-0 w-full relative"
                  style={{ opacity: locked ? 0.75 : 1 }}
                >
                  {/* Color header */}
                  <div className={`bg-gradient-to-br ${game.color} p-4 text-white relative`}>
                    {locked && (
                      <div
                        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-sm"
                        style={{ background: 'rgba(0,0,0,0.3)' }}
                      >
                        🔒
                      </div>
                    )}
                    <div className="text-3xl mb-1">{game.icon}</div>
                  </div>

                  {/* Info */}
                  <div className="px-3 py-3 bg-white">
                    <p className="text-sm font-black text-gray-800 leading-tight">{game.name}</p>
                    <div className="mt-2">
                      {locked ? (
                        <span
                          className="text-xs font-black px-2 py-0.5 rounded-full"
                          style={{ background: '#FFF0B3', color: '#B8860B' }}
                        >
                          Premium
                        </span>
                      ) : (
                        <span
                          className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                          style={{ background: '#58CC02' }}
                        >
                          Žaisti &rarr;
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {filteredGames.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">🔍</div>
              <p className="font-bold">Nerasta žaidimų pagal paiešką</p>
            </div>
          )}
        </section>

        {/* ── Upgrade CTA (subtilus) ─────────────────────────────── */}
        {userAccessLevel === 0 && (
          <div
            className="mt-10 rounded-3xl p-6 flex items-center justify-between gap-4 animate-slide-up"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <div className="text-white">
              <p className="font-black text-lg">🚀 Atrakink visus žaidimus!</p>
              <p className="text-sm opacity-80 mt-0.5">Premium – pilna prieiga prie 50+ žaidimų</p>
            </div>
            <button
              onClick={() => navigate('/subscription')}
              className="flex-shrink-0 px-5 py-2.5 rounded-2xl font-black text-sm"
              style={{ background: 'white', color: '#764ba2' }}
            >
              Sužinoti daugiau
            </button>
          </div>
        )}
      </main>

      {/* Upgrade Modal */}
      {upgradeType && (
        <UpgradePrompt type={upgradeType} onClose={() => setUpgradeType(null)} />
      )}
    </div>
  );
}